import { zipSync, strToU8 } from 'fflate';
import {
  setScenarioArchiveDownloader,
  useRecorderStore,
  type RecorderPrivacyOptions,
} from '../recording/useRecorderStore';
import type { RecorderSnapshot } from '../recording/scenarioRecorder';
import { recorderDiagnosticsLog, type RecorderDiagnosticsLog } from '../recording/recorderDiagnosticsLog';
import { RecorderEdgeCases, recorderEdgeCases } from '../recording/recorderEdgeCases';
import {
  buildScenarioArchive,
  type ScenarioArchiveInput,
  type ScenarioArchiveResult,
} from './scenarioArchiveBuilder';
import { redactArchiveFiles, type RedactionOptions, type RedactionReport } from './scenarioRedaction';

/** Monotonic clock when available; falls back to wall-clock for build-time measurement. */
function nowMs(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

/**
 * Archive size at/above which the user is warned the scenario is large and
 * Lightweight mode may be preferable (spec §20 p.7). A full archive bundles the
 * Revit model (tens of MB), so the threshold sits above a typical model copy.
 */
export const SCENARIO_SIZE_WARNING_BYTES = 50 * 1024 * 1024;

/** Zip the assembled file map into a single archive byte buffer (store + deflate). */
export function zipScenarioArchive(files: Record<string, string | Uint8Array>): Uint8Array {
  const zippable: Record<string, Uint8Array> = {};
  for (const [path, content] of Object.entries(files)) {
    zippable[path] = typeof content === 'string' ? strToU8(content) : content;
  }
  return zipSync(zippable);
}

/** Raised when the mandatory-contents validation fails — surfaced by the recorder UI. */
export class ScenarioArchiveIncompleteError extends Error {
  constructor(public readonly missing: string[], public readonly unresolvedRefs: string[]) {
    super(buildMessage(missing, unresolvedRefs));
    this.name = 'ScenarioArchiveIncompleteError';
  }
}

function buildMessage(missing: string[], unresolvedRefs: string[]): string {
  const parts: string[] = [];
  if (missing.length > 0) {
    parts.push(`missing required files: ${missing.join(', ')}`);
  }
  if (unresolvedRefs.length > 0) {
    parts.push(`unresolved references: ${unresolvedRefs.join(', ')}`);
  }
  return `Scenario archive is incomplete — ${parts.join('; ')}.`;
}

/**
 * Build + validate the archive and return the zip bytes. Throws
 * {@link ScenarioArchiveIncompleteError} when the mandatory-contents validation
 * fails, so the caller surfaces exactly what is missing before finalizing.
 */
export interface PackScenarioOptions {
  /**
   * Produce a clearly-flagged partial archive instead of throwing when the
   * mandatory-contents validation fails. Used when stopping a recording that hit
   * an edge case — the recording can always be stopped and still yield an archive.
   */
  allowPartial?: boolean;
  /** Privacy/redaction applied to scenario.json + all artifacts before zipping. */
  redaction?: RedactionOptions;
  /**
   * Diagnostics log to record the archive build time + final size into
   * (spec §21). Defaults to no logging; the browser download path passes the
   * app-wide recorder log. Injectable for tests.
   */
  diagnostics?: RecorderDiagnosticsLog;
  /** Byte size at/above which {@link SCENARIO_SIZE_WARNING_BYTES} flags an oversize archive. */
  sizeWarningThresholdBytes?: number;
  /** Edge-case handler used to surface an oversize-archive warning. Defaults to the app singleton. */
  edge?: RecorderEdgeCases;
}

export function packScenarioArchive(
  input: ScenarioArchiveInput,
  options: PackScenarioOptions = {},
): { result: ScenarioArchiveResult; zip: Uint8Array; redactionReport?: RedactionReport; sizeBytes: number; oversize: boolean } {
  const startedAt = nowMs();
  const result = buildScenarioArchive(input);
  if (!result.validation.ok && !options.allowPartial) {
    throw new ScenarioArchiveIncompleteError(
      result.validation.missing,
      result.validation.unresolvedRefs,
    );
  }

  let files = result.files;
  let redactionReport: RedactionReport | undefined;
  if (options.redaction) {
    const redacted = redactArchiveFiles(files, options.redaction);
    files = redacted.files;
    redactionReport = redacted.report;
    result.files = files; // the returned result reflects what was actually zipped
  }

  const zip = zipScenarioArchive(files);
  // §21: log archive build time + final size after the archive is assembled.
  options.diagnostics?.archiveBuilt(Math.round(nowMs() - startedAt), zip.length);

  const threshold = options.sizeWarningThresholdBytes ?? SCENARIO_SIZE_WARNING_BYTES;
  const oversize = zip.length >= threshold;
  return { result, zip, redactionReport, sizeBytes: zip.length, oversize };
}

function fileName(input: ScenarioArchiveInput): string {
  const safe = (input.scenario.name || 'scenario').replace(/[^A-Za-z0-9._-]+/g, '_');
  return `${safe}.zip`;
}

/**
 * Browser-side: build, zip and trigger a download of the scenario archive.
 * Guarded so it is a no-op outside a DOM (it is the thin shell over
 * {@link packScenarioArchive}, which holds the testable logic).
 */
export function downloadScenarioArchive(
  input: ScenarioArchiveInput,
  options: PackScenarioOptions = {},
): ScenarioArchiveResult {
  // Default the build-time/size telemetry to the app-wide recorder log.
  const { result, zip, sizeBytes, oversize } = packScenarioArchive(input, {
    diagnostics: recorderDiagnosticsLog,
    ...options,
  });

  // §20 p.7: warn (never block) when the archive is large.
  if (oversize) {
    (options.edge ?? recorderEdgeCases).report('scenario-too-large', { sizeBytes });
  }

  if (typeof document === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) {
    return result;
  }

  const blob = new Blob([zip as unknown as BlobPart], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName(input);
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
  return result;
}

/**
 * Wire the archive builder into the recorder's download seam. The app supplies
 * a collector that turns the recorder snapshot + name/description into a full
 * {@link ScenarioArchiveInput} (gathering settings, elements, model, logs).
 */
export function registerScenarioArchiveDownloader(
  collect: (snapshot: RecorderSnapshot, meta: { name: string; description?: string }) => ScenarioArchiveInput,
): void {
  setScenarioArchiveDownloader((snapshot, meta, privacy) => {
    // Surface an oversize-archive warning through the recorder store (UI) + log.
    const edge = new RecorderEdgeCases(recorderDiagnosticsLog, {
      onUserMessage: (outcome) =>
        outcome.severity === 'error'
          ? useRecorderStore.getState().setError(outcome.userMessage)
          : useRecorderStore.getState().addWarning(outcome.userMessage),
    });
    // Card G: fold the recorder-captured screenshots (key steps + final-state)
    // into the archive input unless the collector already supplied its own, so
    // they reach buildScenarioArchive regardless of the collect implementation.
    const input = collect(snapshot, meta);
    const enriched: ScenarioArchiveInput = {
      ...input,
      screenshots: input.screenshots ?? snapshot.screenshots,
      screenshotRefs: input.screenshotRefs ?? snapshot.screenshotRefs,
    };
    downloadScenarioArchive(enriched, {
      redaction: toRedactionOptions(snapshot, privacy),
      edge,
    });
  });
}

/**
 * Map the recorder's presentation-layer privacy toggles onto the sanitizer's
 * {@link RedactionOptions}. The capture mode (Full/Lightweight) is recorded as
 * the detail level so the scenario stays reproducible (spec §19.7).
 */
function toRedactionOptions(
  snapshot: RecorderSnapshot,
  privacy?: RecorderPrivacyOptions,
): RedactionOptions {
  return {
    excludeAbsolutePaths: privacy?.excludeAbsolutePaths,
    maskLocalDirectories: privacy?.maskLocalDirectories,
    maskUsername: privacy?.maskUsername,
    disableModelSave: privacy?.disableModelSave,
    disableBigPayloadSave: privacy?.disableBigPayloadSave,
    detailLevel: snapshot.mode,
  };
}
